import type { ActionType, ProColumns } from '@ant-design/pro-components';
import { ProTable } from '@ant-design/pro-components';
import { history } from '@umijs/max';
import { Modal, Tag } from 'antd';
import React, { useRef } from 'react';
import {
  getNotificationList,
  markNotificationRead,
} from '@/services/sys-notification/api';
import { formatDateTime } from '@/utils/date';

type NoticeListModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUnreadChange?: () => void;
};

const NoticeListModal: React.FC<NoticeListModalProps> = ({
  open,
  onOpenChange,
  onUnreadChange,
}) => {
  const actionRef = useRef<ActionType>(null);

  const handleMarkRead = async (id: number) => {
    const res = await markNotificationRead({ ids: [id] });
    if (res?.success) {
      actionRef.current?.reload();
      onUnreadChange?.();
    }
  };

  const handleView = async (record: SysNotificationAPI.NotificationItem) => {
    if (!record.isRead) {
      await markNotificationRead({ ids: [record.id] });
      onUnreadChange?.();
    }
    onOpenChange(false);
    if (record.refType === 'sign_publish' && record.refId) {
      history.push(`/sign-manage/published?id=${record.refId}`);
    }
  };

  const columns: ProColumns<SysNotificationAPI.NotificationItem>[] = [
    {
      title: '标题',
      dataIndex: 'title',
      width: 180,
      ellipsis: true,
      render: (_, record) => (
        <span style={{ fontWeight: record.isRead ? undefined : 600 }}>
          {record.title}
        </span>
      ),
    },
    {
      title: '内容',
      dataIndex: 'content',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'isRead',
      width: 80,
      render: (_, record) =>
        record.isRead ? <Tag>已读</Tag> : <Tag color="blue">未读</Tag>,
    },
    {
      title: '时间',
      dataIndex: 'createTime',
      width: 170,
      render: (_, record) => formatDateTime(record.createTime),
    },
    {
      title: '操作',
      valueType: 'option',
      width: 160,
      render: (_, record) => {
        const actions: React.ReactNode[] = [];
        if (!record.isRead) {
          actions.push(
            <a key="read" onClick={() => handleMarkRead(record.id)}>
              标为已读
            </a>,
          );
        }
        if (record.refType === 'sign_publish' && record.refId) {
          actions.push(
            <a key="view" onClick={() => handleView(record)}>
              查看详情
            </a>,
          );
        }
        return actions.length ? actions : <span style={{ color: '#bbb' }}>-</span>;
      },
    },
  ];

  return (
    <Modal
      title="全部消息"
      open={open}
      onCancel={() => onOpenChange(false)}
      footer={null}
      width={820}
      destroyOnHidden
    >
      <ProTable<SysNotificationAPI.NotificationItem>
        actionRef={actionRef}
        rowKey="id"
        size="small"
        search={false}
        options={false}
        toolBarRender={false}
        request={async (params) => {
          const res = await getNotificationList({
            current: params.current,
            pageSize: params.pageSize,
          });
          return {
            data: res?.data ?? [],
            total: res?.total ?? 0,
            success: res?.success,
          };
        }}
        columns={columns}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
        }}
      />
    </Modal>
  );
};

export default NoticeListModal;
